import React, { useState, useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Add, Remove } from '@mui/icons-material';
import TextInput from '../../../../elements/TextInput';
import { addTestSteps, editTestStep } from '../../../../../modules/teststep';
import Modal from '../../../../elements/Modal';
import { Button, Typography } from '@mui/material';

const initialScript = { step: '', testData: '', expectedResult: '', uri: '' };

const TestScriptsFormModal = ({
  show,
  handleClose,
  script,
  fetchScripts,
  testCaseId,
  addNotification,
  mode,
  totalScripts,
  technology_layer
}) => {

  const [formData, setFormData] = useState({ scripts: [initialScript] });
  const [stepNos, setStepNos] = useState(1);

  useEffect(() => {
    if (mode === 'edit') {
      setFormData({ scripts: [script] });
      setStepNos(script?.sNo);
    } else {
      setFormData({ scripts: [initialScript] });
      setStepNos(totalScripts + 1);
    }
  }, [script, mode]);

  const handleAddScript = () => {
    setFormData((prevState) => ({
      scripts: [...prevState.scripts, initialScript],
    }));
  };

  const handleDeleteScript = (index) => {
    setFormData((prevState) => ({
      scripts: prevState.scripts.filter((_, i) => i !== index),
    }));
  };

  const handleChange = (id, value, index, field) => {
    setFormData((prevState) => ({
      ...prevState,
      scripts: prevState.scripts.map((script, i) =>
        i === index ? { ...script, [field]: value } : script
      ),
    }));
  };

  const handleSubmit = async () => {
    const testSteps = {
      testSteps: formData.scripts.map(
        ({ id, step, uri, testData, expectedResult }) => ({
          id: id || null,
          step,
          testData,
          expectedResult,
          uri: uri || ''
        })
      ),
    };

    try {
      await addTestSteps(testSteps, testCaseId);
      addNotification('success', 'Test steps updated successfully!');
      handleClose();
      fetchScripts();
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to update test step. Please try again.';
      addNotification('danger', errorMessage);
    }
  };

  const handleEditSubmit = async () => {
    const step = formData.scripts.find((scriptA) => scriptA.id === script.id);
    const testStep = {
      ...step,
      step: step.step.trim(),
      testData: step.testData.trim(),
      expectedResult: step.expectedResult.trim(),
    };
    try {
      await editTestStep({ testStep }, script.id);
      addNotification('success', 'Test steps updated successfully!');
      handleClose();
      fetchScripts();
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to update test step. Please try again.';
      addNotification('danger', errorMessage);
    }
  };

  const isSaveDisabled = formData?.scripts.some((script) => !script?.step.trim());

  return (
    <Modal
      open={show}
      width="xl"
      handleClose={handleClose}
      modalTitle={mode === 'edit' ? 'Edit Test Step' : 'Add Test Steps'}
      action={() => (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={mode === 'edit' ? handleEditSubmit : handleSubmit}
            disabled={isSaveDisabled}
            title={
              isSaveDisabled ? 'Please provide all required fields' : 'Save'
            }>
            Save
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            Close
          </Button>
        </>
      )}>
      <>
        {formData.scripts.map((script, index) => (
          <Row key={index} className="mb-3 align-items-center">
            <Typography variant="subtitle1" color="textSecondary">
              Step {stepNos + index}
            </Typography>
            <Col>
              <Form.Group>
                <Form.Label className='required'>Description</Form.Label>
                <TextInput
                  id="step"
                  type="textarea"
                  rows={4}
                  value={script?.step}
                  onChange={(id, newValue) =>
                    handleChange(id, newValue, index, 'step')
                  }
                  placeholder="Enter step description"
                  maxLength={2000}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Test Data</Form.Label>
                <TextInput
                  id="testData"
                  type="textarea"
                  rows={4}
                  value={script?.testData}
                  onChange={(id, newValue) =>
                    handleChange(id, newValue, index, 'testData')
                  }
                  placeholder="Enter test data"
                  maxLength={2000}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Expected Result</Form.Label>
                <TextInput
                  id="expectedResult"
                  type="textarea"
                  rows={4}
                  value={script?.expectedResult}
                  onChange={(id, newValue) =>
                    handleChange(id, newValue, index, 'expectedResult')
                  }
                  placeholder="Enter expected result"
                  maxLength={2000}
                />
              </Form.Group>
            </Col>
            {technology_layer && technology_layer === 'API/Integration' && <Col>
              <Form.Group>
                <Form.Label>URI</Form.Label>
                <TextInput
                  id="uri"
                  type="textarea"
                  rows={4}
                  value={script?.uri}
                  onChange={(id, newValue) =>
                    handleChange(id, newValue, index, 'uri')
                  }
                  placeholder="Enter URI"
                  maxLength={250}
                />
              </Form.Group>
            </Col>}
            <Row>
            {mode === 'add' && (
              <Col className="d-flex align-items-center justify-content-end">
                {index > 0 && (
                  <Button
                    variant=""
                    size="sm"
                    title="Remove Step"
                    onClick={() => handleDeleteScript(index)}
                    style={{ color: 'red' }}>
                    <Remove color="red" /> Remove
                  </Button>
                )}
                {index === formData.scripts.length - 1 && (
                  <Button
                    variant=""
                    size="sm"
                    onClick={handleAddScript}
                    title="Add">
                    <Add color="secondary" /> Add New Step
                  </Button>
                )}
              </Col>
            )}
            </Row>
           
          </Row>
        ))}
      </>
    </Modal>
  );
};

export default TestScriptsFormModal;
